import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { BaseComponent } from '../../base/base.component';
import { UserCoiService } from '../../services/user-coi/user-coi.service';
import { UserMicroData, UserMicroData_CircleDataExtended, UserRole } from '../../services/personal-user-data/personal-user-data';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-select-users-from-list',
  templateUrl: './select-users-from-list.component.html',
  styleUrls: ['./select-users-from-list.component.scss']
})
export class SelectUsersFromListComponent extends BaseComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  @Input() selectedUsersIds: Set<string> = new Set(); 

  possibleUsers: UserMicroData_CircleDataExtended[] = [];
  selectedUsers: UserMicroData_CircleDataExtended[] = [];

  userRoleInstance = UserRole;
  isLoading: boolean = false;
  secondLevelAdded : boolean = false;
  secondLevelUsers: UserMicroData_CircleDataExtended[] = [];

  // Create a FormControl for the search input.
  searchCtrl = new FormControl('');
  filteredUsers!: Observable<UserMicroData_CircleDataExtended[]>;

  constructor(private userCoiService: UserCoiService, 
              private dialogRef: MatDialogRef<SelectUsersFromListComponent>)
              {
    super();
  }

  ngOnInit(): void {
    this.isLoading = true;

    this.userCoiService.getUsersToExpandPersonOfInterest().subscribe((users: UserMicroData_CircleDataExtended[]) => {

      this.possibleUsers = users.filter(user => user.id !== this.user!.id);
      this.possibleUsers = this.sortUsers(this.possibleUsers);
      this.filteredUsers = this.searchCtrl.valueChanges.pipe(
        startWith(''),
        map((value: string | null) => this.filterUsers(value || ''))
      );

      if (this.selectedUsersIds.size > 0) {
        this.selectedUsersIds.forEach(userId => {
          const user = this.possibleUsers.find(user => user.id === userId);
          if (user) {
            this.selectedUsers.push(user);
          }
        });
      }

      this.isLoading = false;
    });
}

sortUsers(list: UserMicroData_CircleDataExtended[]) : UserMicroData_CircleDataExtended[] {
  return list.sort((a, b) => {
    // 1) put those with existsInUserCircle === true first
    if (a.existsInUserCircle !== b.existsInUserCircle) {
      return a.existsInUserCircle ? -1 : 1;
    }
    // 2) if both in the same group, sort by full name A→Z
    const nameA = `${a.firstName} ${a.lastName}`;
    const nameB = `${b.firstName} ${b.lastName}`;
    return nameA.localeCompare(nameB);
  });
}

  displayFn(user: UserMicroData_CircleDataExtended): string {
    return user ? `${user.firstName} ${user.lastName}` : '';
  }

  /**
   * Filters the possibleUsers based on the provided search term.
   */
  filterUsers(searchTerm: string): UserMicroData_CircleDataExtended[] {
    if (!searchTerm) {
      return this.possibleUsers;
    }

    const term = typeof searchTerm === 'string' ? searchTerm : this.displayFn(searchTerm);
    const lowerCaseTerm = term.toLowerCase();
    return this.possibleUsers.filter(user =>
      user.firstName.toLowerCase().includes(lowerCaseTerm) ||
      user.lastName.toLowerCase().includes(lowerCaseTerm)
    );
  }

  toggleSelection(user: UserMicroData_CircleDataExtended, selected: boolean): void {
    if (selected) {
      this.addUser(user);
    } else {
      this.removeUser(user);
    }
  }

  /**
   * Adds a user to the selectedUsers list if not already selected.
   */
  addUser(user: UserMicroData_CircleDataExtended): void {
    if (!this.selectedUsers.find(u => u.id === user.id)) {
      this.selectedUsers.push(user);
      this.selectedUsersIds.add(user.id);
    }
    // Clear the search input after adding the user.
    this.searchCtrl.setValue('');
  }

  /**
   * Removes a user from the selectedUsers list.
   */
  removeUser(user: UserMicroData_CircleDataExtended): void {
    this.selectedUsers = this.selectedUsers.filter(u => u.id !== user.id);
    this.selectedUsersIds.delete(user.id);
  }

  selectSecondLevelUsers(): void {

    if (this.secondLevelUsers.length > 0){
      // we already have the second level users
      if (!this.secondLevelAdded){
        this.addSecondLevelUsers();
      }
      else{
        this.removeSecondLevelUsers();
      }
      this.secondLevelAdded = !this.secondLevelAdded;
    }
    else{
      // we don't have the second level users
      this.userCoiService.getUsersUnderNLevels("", 2).subscribe((users: UserMicroData_CircleDataExtended[]) => {
        this.secondLevelUsers = users;

        if (!this.secondLevelAdded){
          this.addSecondLevelUsers();
        }
        else{
          this.removeSecondLevelUsers();
        }

        this.secondLevelAdded = !this.secondLevelAdded;
      });
    }
  }

  addSecondLevelUsers(): void {
    const map = new Map<string, UserMicroData_CircleDataExtended>();

    // combine the selected and the 2ndLevel lists - performance sensitive
      // First add all from `a`
    for (const user of this.selectedUsers) {
      map.set(user.id, user);
    }
    // Then add/overwrite from `b`
    for (const user of this.secondLevelUsers) {
      if (!user.existsInUserCircle){
        map.set(user.id, user);
      }
    }

    this.selectedUsers = Array.from(map.values());
    this.selectedUsersIds = new Set(this.selectedUsers.map(user => user.id));
  }

  removeSecondLevelUsers(): void {
    const removeUsersSet = new Set(this.secondLevelUsers.map(user => user.id));
    this.selectedUsers = this.selectedUsers.filter(user => !removeUsersSet.has(user.id));
    this.selectedUsersIds = new Set(this.selectedUsers.map(user=>user.id));
  }

  onClose(): void {
      this.dialogRef.close();
  }
}

